import { useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import type { MeteredMeasure } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import {
  type ConnectorUsageRow,
  defaultPricePerUnit,
  MEASURE_UNIT,
} from "../../lib/connectorUsage";
import {
  FormCard,
  PriceGrid,
  PriceRow,
  ReadOnlyCell,
  SectionHeading,
  UsdInput,
} from "../AiProviderModal/modalForm";
import { Box } from "../Box";
import { Text } from "../Text";

const inputStyle = { width: "100%", minWidth: 0 } as const;

type Props = {
  connectorId: string;
  rows: readonly ConnectorUsageRow[];
  measures: readonly MeteredMeasure[];
};

export function MeasurePricingSection({ connectorId, rows, measures }: Props) {
  const z = useZero();
  const canManage = useHasAccess("tenant:ai_providers_manage");
  const [priceRows] = useQuery(queries.monitoringUsagePrices());
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const perModelColumns = measures.filter((m) => m.perModel);
  const flatCells = measures.filter((m) => !m.perModel);

  // Per-model rates are keyed by the raw model id the provider returns (what
  // the ledger stores as the dimension): every model this connector has metered,
  // plus any that already carry a price so a rate stays visible after its usage
  // ages out of the window.
  const models = useMemo(() => {
    const perModel = new Set(
      measures.filter((m) => m.perModel).map((m) => m.measure)
    );
    if (perModel.size === 0) return [];
    const set = new Set<string>();
    for (const row of rows) {
      if (perModel.has(row.measure) && row.dimension) set.add(row.dimension);
    }
    for (const row of priceRows ?? []) {
      if (row.connectorId !== connectorId) continue;
      if (row.spaceId != null) continue;
      if (perModel.has(row.measure) && row.dimension) set.add(row.dimension);
    }
    return [...set];
  }, [rows, measures, priceRows, connectorId]);

  const priceByKey = useMemo(() => {
    const map = new Map<string, { id: string; amountPerUnit: number }>();
    for (const row of priceRows ?? []) {
      if (row.connectorId !== connectorId) continue;
      if (row.spaceId != null) continue;
      map.set(`${row.measure} ${row.dimension}`, row);
    }
    return map;
  }, [priceRows, connectorId]);

  const cellKey = (measure: string, dimension: string) =>
    `${measure} ${dimension}`;

  const displayed = (measure: string, dimension: string) => {
    const key = cellKey(measure, dimension);
    if (drafts[key] != null) return drafts[key];
    const stored = priceByKey.get(key)?.amountPerUnit;
    if (stored == null) return "";
    return String(stored * (MEASURE_UNIT[measure]?.per ?? 1));
  };

  const commit = (measure: string, dimension: string) => {
    if (!canManage) return;
    const key = cellKey(measure, dimension);
    const existing = priceByKey.get(key);
    const raw = displayed(measure, dimension).trim();
    const shown = raw === "" ? 0 : Number.parseFloat(raw);
    if (!Number.isFinite(shown)) return;
    const amountPerUnit = shown / (MEASURE_UNIT[measure]?.per ?? 1);
    if (amountPerUnit <= 0) {
      if (existing)
        z.mutate(sharedMutators.deleteUsagePrice({ id: existing.id }));
      return;
    }
    if (existing?.amountPerUnit === amountPerUnit) return;
    z.mutate(
      sharedMutators.updateUsagePrice({
        connectorId,
        measure,
        ...(dimension ? { dimension } : {}),
        amountPerUnit,
      })
    );
  };

  // A known model's list price shows as the placeholder: an unedited field still
  // prices via the same default, and typing overrides it.
  const placeholderFor = (measure: string, dimension: string) => {
    const perUnit = defaultPricePerUnit(measure, dimension);
    if (perUnit == null) return "0.00";
    return String(perUnit * (MEASURE_UNIT[measure]?.per ?? 1));
  };

  const priceInput = (measure: string, dimension: string) => (
    <UsdInput
      value={displayed(measure, dimension)}
      placeholder={placeholderFor(measure, dimension)}
      disabled={!canManage}
      onChange={(next) =>
        setDrafts((prev) => ({ ...prev, [cellKey(measure, dimension)]: next }))
      }
      style={inputStyle}
    />
  );

  const gridTemplate = `minmax(120px, 1.2fr) repeat(${perModelColumns.length}, minmax(0, 1fr))`;

  return (
    <FormCard style={{ gap: 24 }}>
      <SectionHeading
        title="Measure pricing"
        description="Set the price for each unit of usage this connector meters. These rates drive the estimated spend shown in Monitoring and against spend limits. Known models are pre-filled with list pricing — override any that are wrong."
      />
      <PriceGrid>
        {perModelColumns.length > 0 &&
          (models.length > 0 ? (
            <>
              <PriceRow style={{ gridTemplateColumns: gridTemplate }}>
                <Box left={8}>
                  <Text variant="text-small-md">Model</Text>
                </Box>
                {perModelColumns.map((column) => (
                  <Box key={column.measure} left={8}>
                    <Text variant="text-small-md">
                      {MEASURE_UNIT[column.measure]?.name} (
                      {MEASURE_UNIT[column.measure]?.label})
                    </Text>
                  </Box>
                ))}
              </PriceRow>
              {models.map((model) => (
                <PriceRow
                  key={model}
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  <ReadOnlyCell>{model}</ReadOnlyCell>
                  {perModelColumns.map((column) => (
                    <div
                      key={column.measure}
                      onBlur={() => commit(column.measure, model)}
                    >
                      {priceInput(column.measure, model)}
                    </div>
                  ))}
                </PriceRow>
              ))}
            </>
          ) : (
            <Box horizontal={8}>
              <Text variant="text-small" color="var(--text-secondary)">
                Per-model pricing appears here once this connector meters usage.
              </Text>
            </Box>
          ))}
        {flatCells.map((cell) => (
          <PriceRow
            key={cell.measure}
            style={{
              gridTemplateColumns: "minmax(140px, 1fr) minmax(0, 168px)",
            }}
            onBlur={() => commit(cell.measure, "")}
          >
            <ReadOnlyCell>
              {MEASURE_UNIT[cell.measure]?.name} (
              {MEASURE_UNIT[cell.measure]?.label})
            </ReadOnlyCell>
            {priceInput(cell.measure, "")}
          </PriceRow>
        ))}
      </PriceGrid>
    </FormCard>
  );
}
